<script context="module" lang="ts">
    declare const __APP_VERSION__: string;
</script>

<script lang="ts">
    import { onMount } from 'svelte';
    import { link } from 'svelte-spa-router';
    import { location } from '../js/spaLocation';
    import UserAvatar from './UserAvatar.svelte';
    import { auth } from "../js/auth.js";
    import { writable } from 'svelte/store';
    import { push } from 'svelte-spa-router';

    interface ComponentDetail {
        component: any;
        name: string;
        icon: string;
        href?: string;
        onClick?: () => void;
        className?: string;
        id?: string;
        hidden?: boolean;
        group?: string;
    }

    interface Components {
        [key: string]: ComponentDetail;
    }
  
    export let components: Components;
    export let dashboardType = 'Admin';
    
    let currentComponent: any = null;
    let currentComponentName: string | null = null;

    interface VersionInfo {
        build_number?: string;
        commit_hash?: string;
    }

    let versionInfo: VersionInfo | null = null;
    let displayVersion = __APP_VERSION__ || 'dev';

    // Map URL paths to component names for admin dashboard
    const pathToComponentMap: Record<string, string> = {
        '/dashboard-admin': 'AdminHome',
        '/admin/users': 'UserList',
        '/admin/briefs': 'AdminBriefList',
        '/admin/brief-questions': 'BriefQuestionList',
        '/admin/proposal-requests': 'ProposalRequests',
        '/admin/invoices': 'AdminInvoiceList',
        '/admin/billing': 'AdminBilling',
        '/admin/credit-usage': 'AdminCreditUsage',
        '/admin/knowledge/platform': 'AdminPlatformKnowledge',
        '/admin/knowledge/brief-types': 'AdminBriefTypeKnowledgeList',
        '/admin/knowledge/test': 'AdminKnowledgeTest',
        '/admin/activity-log': 'AdminActivityLog',
        '/admin/ai-processes': 'AdminAiJobs',
        '/admin/teams': 'AdminTeamList'
    };

    // Special sub-route mappings (for detail pages)
    const subRouteMap: Record<string, string> = {
        '/admin/invoices/': 'AdminInvoiceDetail',
        '/admin/briefs/': 'AdminBriefDetail',
        '/admin/teams/': 'AdminTeamDetail',
        '/admin/users/': 'AdminUserDetail',
        '/admin/knowledge/brief-types/': 'AdminBriefTypeKnowledge'
    };
    
    // Check if path starts with any mapped route
    function getComponentForPath(path: string): string | null {
        // Direct match first
        if (pathToComponentMap[path]) {
            return pathToComponentMap[path];
        }
        // Check for special sub-routes (detail pages)
        for (const [prefix, componentName] of Object.entries(subRouteMap)) {
            if (path.startsWith(prefix) && path.length > prefix.length) {
                return componentName;
            }
        }
        // Check for general sub-routes (e.g., /admin/briefs/123)
        for (const [route, componentName] of Object.entries(pathToComponentMap)) {
            if (path.startsWith(route + '/')) {
                return componentName;
            }
        }
        return null;
    }

    function routeTo(componentName: string) {
        currentComponentName = components[componentName]?.name || null;
        currentComponent = components[componentName]?.component || null;
        
        // Update URL if component has a route
        const routePath = Object.entries(pathToComponentMap).find(([_, comp]) => comp === componentName)?.[0];
        if (routePath && routePath !== $location) {
            push(routePath);
        }
    }

    // Sync component with current URL - reactive to location changes
    $: if ($location) {
        const path = $location;
        const componentName = getComponentForPath(path);
        if (componentName && components[componentName]) {
            // Only update if component actually changed
            if (currentComponentName !== componentName) {
                currentComponentName = components[componentName].name || null;
                currentComponent = components[componentName].component || null;
            }
        } else if (path === '/dashboard-admin' || path === '/dashboard-admin/') {
            // Default to AdminHome if on base admin dashboard route
            if (components['AdminHome'] && currentComponentName !== 'AdminHome') {
                currentComponentName = components['AdminHome'].name || null;
                currentComponent = components['AdminHome'].component || null;
            }
        }
    }

    export let authData: any = writable([]);
    auth.subscribe((data: any) => {
        authData = data;
    });


    onMount(() => {
        void (async () => {
            try {
                const response = await fetch('/version.json');
                if (response.ok) {
                    versionInfo = await response.json();
                    if (versionInfo?.build_number) {
                        displayVersion = versionInfo.build_number;
                    }
                }
            } catch {
                // Version info not available in local dev
            }
        })();

        // Initialize component based on current URL
        const path = $location || '/dashboard-admin';
        const componentName = getComponentForPath(path);
        if (componentName) {
            currentComponentName = components[componentName]?.name || null;
            currentComponent = components[componentName]?.component || null;
        } else {
            // Default to first component if no matching route
            const firstComponent = Object.keys(components).find(key => components[key].component);
            if (firstComponent) {
                routeTo(firstComponent);
            }
        }
    });
</script>



<div class="dashboard admin-dashboard" data-dashboard-type={dashboardType}>
    <div class="menu-left">
        <div class="menu-left-header">
            <div class="admin-account-card">
                <a use:link href="/" class="admin-logo" title="Xplorit Home" aria-label="Xplorit Home">
                    <i class="fas fa-shield-alt" aria-hidden="true"></i>
                </a>
                <UserAvatar
                    firstName={authData.user.first_name}
                    lastName={authData.user.last_name}
                    email={authData.user.email}
                    imageUrl={authData.user.profile_image_url}
                    size={36}
                />
                <div class="admin-account-meta">
                    <div class="name">
                        {#if authData.user.first_name || authData.user.last_name}
                            {authData.user.first_name || ''} {authData.user.last_name || ''}
                        {:else}
                            {authData.user.email || 'Admin'}
                        {/if}
                    </div>
                    <div class="role-badge">Admin</div>
                </div>
            </div>
        </div>

        <div class="menu-left-content">
            {#each Object.entries(components) as [componentName, item], idx}
                {@const prevVisible = Object.values(components).slice(0, idx).reverse().find((c) => c && !c.hidden && c.name !== 'Logout') ?? null}
                {@const visibleGroup = item.group && !item.hidden && item.name !== 'Logout' ? item.group : null}
                {@const prevGroup = prevVisible?.group ?? null}
                {@const showGroupHeading = !!visibleGroup && visibleGroup !== prevGroup}

                {#if showGroupHeading}
                    <div class="menu-group" role="group" aria-label={visibleGroup}>
                        <span class="menu-group-label">{visibleGroup}</span>
                    </div>
                {/if}

                {#if item.name !== 'Logout' && !item.hidden}
                    {@const itemHref = item.href ?? ''}
                    {#if itemHref}
                        <a use:link
                           href={itemHref}
                           class="item"
                           class:active={$location === itemHref || $location.startsWith(itemHref + '/') || ($location === '/dashboard-admin' && itemHref === '/dashboard-admin' && componentName === 'AdminHome')}
                           on:click={() => {
                               const compName = pathToComponentMap[itemHref] || componentName;
                               if (components[compName]) {
                                   currentComponentName = components[compName].name || null;
                                   currentComponent = components[compName].component || null;
                               }
                           }}>
                            <i class={item.icon} id={item.id}></i>
                            <span>{item.name}</span>
                        </a>
                    {:else}
                        <button type="button"
                           class="item"
                           class:active={currentComponentName === item.name}
                           on:click={() => routeTo(componentName)}>
                            <i class={item.icon} id={item.id}></i>
                            <span>{item.name}</span>
                        </button>
                    {/if}
                {/if}
            {/each}
            <!-- Logout button at the bottom, separated -->
            <div class="logout-section">
                {#each Object.entries(components) as [componentName, {name, icon, id, onClick, href, className}]}
                    {#if name === 'Logout' && onClick}
                        <div class="menu-divider"></div>
                        <button type="button"
                           class="item logout-item"
                           on:click={onClick}>
                            <i class={icon} id={id}></i>
                            <span>{name}</span>
                        </button>
                    {/if}
                {/each}
            </div>
            <!-- Version display at bottom of menu -->
            <div class="version-display">
                <span>v{displayVersion}{#if versionInfo?.commit_hash} ({versionInfo.commit_hash}){/if}</span>
            </div>
        </div>
    </div>
    <div class="ui content">
        {#if currentComponent}
            {#key currentComponentName}
                <svelte:component this={currentComponent} />
            {/key}
        {/if}
    </div>

    <div class="menu-bottom">
        {#each Object.entries(components) as [componentName, {name, icon, id, onClick, href, className, hidden}]}
            {#if name !== 'Logout' && !hidden}
                {#if href}
                    <a use:link
                       href={href}
                       class="item"
                       class:active={$location === href || $location.startsWith(href + '/') || ($location === '/dashboard-admin' && href === '/dashboard-admin' && componentName === 'AdminHome')}
                       on:click={() => {
                           const compName = pathToComponentMap[href] || componentName;
                           if (components[compName]) {
                               currentComponentName = components[compName].name || null;
                               currentComponent = components[compName].component || null;
                           }
                       }}>
                        <i class={icon} id={id}></i>
                        <span>{name}</span>
                    </a>
                {:else}
                    <button type="button"
                    class="item"
                    class:active={currentComponentName === name}
                    on:click={() => routeTo(componentName)}>
                        <i class={icon} id={id}></i>
                        <span>{name}</span>
                    </button>
                {/if}
            {/if}
        {/each}
    </div>
</div>

<style>
    :global(.dashboard .menu-left-content) {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .menu-group {
        padding: var(--space-3) var(--space-2) var(--space-1);
        flex-shrink: 0;
    }

    .menu-left-content > .menu-group:first-child {
        padding-top: var(--space-2);
    }

    .menu-group-label {
        font-size: 0.65rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--grey-secondary);
    }

    .menu-left-content {
        display: flex;
        flex-direction: column;
        flex: none;
        min-height: auto;
        overflow: visible;
        padding-left: 0;
        padding-right: 0;
    }

    .menu-left-content > :global(.item:not(.logout-item)) {
        flex-shrink: 0;
    }

    .admin-account-card {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        width: 100%;
        min-width: 0;
        padding: var(--space-2);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-sizing: border-box;
        overflow: hidden;
    }

    /* Global `.dashboard .menu-left a { width: 100% }` otherwise stretches this
       link across the card and pushes the avatar out of the sidebar. */
    :global(.dashboard.admin-dashboard .menu-left) .admin-logo {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 2rem;
        width: 2rem;
        max-width: 2rem;
        height: 2rem;
        color: var(--primary);
        text-decoration: none;
    }

    .admin-logo i {
        font-size: 1.125rem;
        line-height: 1;
    }

    .admin-account-meta {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .admin-account-meta .name {
        font-size: var(--text-sm);
        font-weight: 500;
        color: var(--grey);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.25;
    }

    .role-badge {
        display: inline-block;
        background: var(--primary);
        color: var(--white);
        font-size: 0.6rem;
        font-weight: 500;
        padding: 1px 6px;
        border-radius: 999px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        line-height: 1.4;
        width: fit-content;
    }

    .item {
        display: flex !important;
        align-items: center !important;
        gap: 12px;
        text-align: start;
        padding: 10px !important;
        padding-left: 10px !important;
        padding-right: 10px !important;
        text-decoration: none !important;
        color: var(--black) !important;
        border-radius: 5px !important;
        font-weight: 500 !important;
        cursor: pointer;
        transition: background-color 0.2s, color 0.2s;
        width: 100%;
        box-sizing: border-box;
        border: none !important;
        background: none !important;
        box-shadow: none !important;
        margin: 0 !important;
        position: relative;
        min-width: 0;
    }

    .item i.icon {
        margin: 0 !important;
        width: 20px;
        min-width: 20px;
        max-width: 20px;
        text-align: center;
        flex-shrink: 0;
        background: none !important;
        border: none !important;
        padding: 0 !important;
        display: inline-block;
        line-height: 1;
    }

    .item span {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
    }

    .item:hover {
        color: var(--white) !important;
        background-color: var(--darkprimary) !important;
    }

    .item.active {
        background-color: var(--primary) !important;
        color: var(--white) !important;
    }

    .item.active:hover {
        background-color: var(--primary) !important;
        color: var(--white) !important;
    }

    .menu-divider {
        height: 1px;
        background-color: var(--border-color);
        margin: 8px 0;
        flex-shrink: 0;
        width: 100%;
        display: block;
    }

    .logout-section {
        margin-top: var(--space-4);
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .logout-item {
        flex-shrink: 0;
    }

    .version-display {
        margin-top: 0;
        padding: 8px 16px;
        text-align: center;
        font-size: 0.65rem;
        color: var(--grey-secondary);
        opacity: 0.6;
        border-top: 1px solid var(--border-color);
        flex-shrink: 0;
    }

    .version-display span {
        display: block;
    }
</style>
